/**
 * IntakePendingCard — dotted-outline card surfaced on the Plans screen
 * when the caller has one or more `intake_pending` projects.
 *
 *   ┌─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ┐
 *   │ ┌──┐ DRAFT PLAN — FINISH SETUP                          [×] │
 *   │ │✨│ Steve Jones                                              │
 *   │ └──┘ Delaware · 26                                            │
 *   │                                                                │
 *   │  Angelman Syndrome   Arthritis   Bladder Dysfunction   +3     │
 *   │                                                                │
 *   │  📄 sample_care_plan_recipient.pdf                           │
 *   │  ───────────────────────────────────────────────              │
 *   │  💊 2 meds   👤 1 contact            Started 5m ago           │
 *   │                                              Continue →       │
 *   └─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ┘
 *
 * Tap → navigates into ProjectCreationTypeformScreen with
 * `route.params.resumeIntakeId`, which causes the screen to refetch the
 * intake row + hand the seeded state to TypeformRenderer.
 *
 * Visual language: dashed border (not solid) + lower-opacity background so
 * the card reads as "needs action, not a real plan yet." The information
 * density (avatar + name + identity + diagnoses + stats + started-ago) is
 * intentionally the same shape as the real patient card on the same
 * screen — admins shouldn't have to switch reading modes between row
 * types. Same seed-field lookups as IntakeResumePreview so what's shown
 * here matches what they'll see when they tap in.
 */

import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import type { IntakePendingRow } from '../store/actions/projects';
import { describeAge } from '../utilities/formatAge';

export interface IntakePendingCardProps {
  row: IntakePendingRow;
  onPress: (projectId: number | string) => void;
  onDismiss?: (projectId: number | string) => void;
}

function calcAgeYears(birthday: any): number | null {
  if (!birthday) return null;
  const d = new Date(String(birthday));
  if (!Number.isFinite(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  if (ms <= 0) return null;
  return Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
}

function timeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

/** The person a plan draft is for, from what was typed or read so far. */
export function intakeDraftName(row: IntakePendingRow): string {
  const seed: Record<string, any> = { ...(row.blob?.intake_extracted?.extracted || {}), ...(row.blob?.intake_progress?.data || {}) };
  const firstName: string = seed.first_name || seed.self_first_name || '';
  const lastName: string = seed.last_name || seed.self_last_name || '';
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

export default function IntakePendingCard({ row, onPress, onDismiss }: IntakePendingCardProps) {
  const theme = useTheme();

  // Seed merge order mirrors IntakeResumePreview's: extraction underneath,
  // user-typed progress on top, so anything the user has edited wins over
  // the AI's initial guess. Same `self_*` fallbacks as that screen.
  const extracted = row.blob?.intake_extracted?.extracted || {};
  const progress = row.blob?.intake_progress?.data || {};
  const seed: Record<string, any> = { ...extracted, ...progress };

  const firstName: string = seed.first_name || seed.self_first_name || '';
  const lastName: string = seed.last_name || seed.self_last_name || '';
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  const birthday = seed.birthday || seed.self_birthday || null;
  const age = calcAgeYears(birthday);
  const stateAbbrev: string = seed.state || seed.self_state || '';

  const diagnoses: string[] = Array.isArray(seed.diagnoses_select_all)
    ? seed.diagnoses_select_all.filter((d: any) => typeof d === 'string' && d.trim())
    : [];
  const visibleDiags = diagnoses.slice(0, 3);
  const extraDiags = diagnoses.length - visibleDiags.length;

  const docFilename = row.document?.filename || '';
  const docStatus = String(row.document?.status || '').toLowerCase();
  const docFailed = !!row.document && (docStatus === 'failed' || docStatus === 'failed_extraction');
  const docProcessing = !!row.document && docStatus === 'processing';

  const identityParts: string[] = [];
  if (stateAbbrev) identityParts.push(String(stateAbbrev));
  // describeAge returns '' for a value that is not a real age; an empty part
  // would render as a dangling separator, so it is dropped rather than joined.
  const agePart = age != null ? describeAge(age) : '';
  if (agePart) identityParts.push(agePart);

  // Status line about what the processor found / is doing. Skipped when
  // the footer stats already convey the same counts, to avoid the same
  // number showing up in two places on the same card.
  let statusNote = '';
  if (docProcessing) statusNote = 'Still reading your document…';
  else if (docFailed) statusNote = "We couldn't read this document — pick up manually";
  else if (row.medication_count === 0 && row.contact_count === 0 && !docFilename) {
    statusNote = 'Tap to finish setup';
  }

  const startedAgo = timeAgo(row.created_at);
  const accent = (docFailed ? theme.colors.error : theme.colors.primary) as string;

  return (
    <TouchableOpacity
      onPress={() => onPress(row.project_id)}
      activeOpacity={0.85}
      style={[
        styles.card,
        { borderColor: accent, backgroundColor: `${accent}08` },
      ]}
    >
      {/* Header — avatar + name block + dismiss */}
      <View style={styles.headerRow}>
        <View style={[styles.avatar, { backgroundColor: `${accent}1f`, borderColor: `${accent}33` }]}>
          <Icon
            icon={['fas', docFailed ? 'triangle-exclamation' : 'sparkles']}
            size={20}
            color={accent}
          />
        </View>
        <View style={styles.nameBlock}>
          <Text style={[styles.eyebrow, { color: accent }]} numberOfLines={1}>
            {docFailed ? 'Draft plan — needs attention' : 'Draft plan — finish setup'}
          </Text>
          <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>
            {name || 'New plan'}
          </Text>
          {identityParts.length > 0 ? (
            <Text style={[styles.identityLine, { color: theme.colors.muted }]} numberOfLines={1}>
              {identityParts.join(' · ')}
            </Text>
          ) : null}
        </View>
        {onDismiss ? (
          <TouchableOpacity
            onPress={() => onDismiss(row.project_id)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.dismissBtn}
          >
            <Icon icon={['fas', 'xmark']} size={14} color={theme.colors.muted as string} />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Diagnoses — same chip shape as ProjectCard, tinted to the
          card's accent so visited drafts read as a coherent block
          rather than a borrowed slice of the live-plan UI. */}
      {visibleDiags.length > 0 ? (
        <View style={styles.tagsRow}>
          {visibleDiags.map((diag, i) => (
            <View
              key={`${diag}-${i}`}
              style={[styles.diagTag, { backgroundColor: `${accent}14`, borderColor: `${accent}33` }]}
            >
              <Text style={[styles.diagTagText, { color: accent }]} numberOfLines={1}>
                {diag}
              </Text>
            </View>
          ))}
          {extraDiags > 0 ? (
            <View style={[styles.diagTag, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <Text style={[styles.diagTagText, { color: theme.colors.muted }]}>+{extraDiags}</Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* Source document filename so admins can see which upload this
          draft came from at a glance. */}
      {docFilename ? (
        <View style={styles.docRow}>
          <Icon
            icon={['fad', docFailed ? 'circle-exclamation' : 'file-lines']}
            size={12}
            color={(docFailed ? theme.colors.error : theme.colors.muted) as string}
          />
          <Text
            style={[styles.docText, { color: (docFailed ? theme.colors.error : theme.colors.muted) }]}
            numberOfLines={1}
          >
            {docFilename}
          </Text>
        </View>
      ) : null}

      {statusNote ? (
        <Text style={[styles.statusNote, { color: theme.colors.muted }]} numberOfLines={2}>
          {statusNote}
        </Text>
      ) : null}

      {/* Footer — stats from the processor + started-ago timestamp. */}
      {(row.medication_count > 0 || row.contact_count > 0 || startedAgo) ? (
        <View style={[styles.footerRow, { borderTopColor: `${accent}25` }]}>
          <View style={styles.footerStats}>
            <FooterStat
              icon={['fad', 'pills']}
              count={row.medication_count}
              singular="med"
              plural="meds"
              theme={theme}
            />
            <FooterStat
              icon={['fad', 'users']}
              count={row.contact_count}
              singular="contact"
              plural="contacts"
              theme={theme}
            />
          </View>
          {startedAgo ? (
            <Text style={[styles.footerUpdated, { color: theme.colors.muted }]}>
              Started {startedAgo}
            </Text>
          ) : null}
        </View>
      ) : null}

      <View style={styles.ctaRow}>
        <Text style={[styles.ctaText, { color: accent }]}>
          {docFailed ? 'Open' : docProcessing ? 'Check progress' : 'Continue'} →
        </Text>
      </View>
    </TouchableOpacity>
  );
}

function FooterStat({
  icon,
  count,
  singular,
  plural,
  theme,
}: {
  icon: [string, string];
  count: number;
  singular: string;
  plural: string;
  theme: ReturnType<typeof useTheme>;
}) {
  if (!count) return null;
  return (
    <View style={styles.footerStat}>
      <Icon icon={icon as any} size={11} color={theme.colors.muted as string} />
      <Text style={[styles.footerStatText, { color: theme.colors.muted }]}>
        {count} {count === 1 ? singular : plural}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 14,
    marginBottom: 12,
    gap: 10,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  nameBlock: { flex: 1, minWidth: 0, gap: 2 },
  eyebrow: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  title: { fontSize: 16, fontWeight: '700' },
  identityLine: { fontSize: 12.5, fontWeight: '500' },
  dismissBtn: { alignSelf: 'flex-start', padding: 2 },

  tagsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  diagTag: {
    borderRadius: 6,
    borderWidth: 1,
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  diagTagText: { fontSize: 11, fontWeight: '500' },

  docRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  docText: { fontSize: 12, flex: 1 },
  statusNote: { fontSize: 13, lineHeight: 18 },

  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 8,
    marginTop: 2,
    gap: 8,
  },
  footerStats: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  footerStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginRight: 10,
  },
  footerStatText: { fontSize: 11 },
  footerUpdated: { fontSize: 11, fontWeight: '500', flexShrink: 0 },

  ctaRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: -2 },
  ctaText: { fontSize: 13, fontWeight: '600' },
});
